import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import OutlinePanel from '../components/Outline/OutlinePanel';
import Loading from '../components/Common/Loading';
import './OutlinePage.css';

/**
 * SeriesOutlinePage - Project-level outline for series planning.
 *
 * Shows the series outline for the currently selected project.
 * Used for planning multi-book arcs.
 *
 * If no project is selected, shows a project selector for series projects.
 */
function SeriesOutlinePage() {
  const { currentProject, setCurrentProject, projects } = useApp();
  const [initialized, setInitialized] = useState(false);

  // Get series projects only (is_series = true)
  const seriesProjects = projects.filter(p => p.is_series);

  // Auto-select first series project if none selected
  useEffect(() => {
    if (!initialized && projects.length > 0) {
      setInitialized(true);
      // If no current project or current project is not a series, select first series
      if (!currentProject || !currentProject.is_series) {
        if (seriesProjects.length > 0) {
          setCurrentProject(seriesProjects[0]);
        }
      }
    }
  }, [initialized, projects, currentProject, seriesProjects, setCurrentProject]);

  // Show loading while projects are being fetched
  if (projects.length === 0 && !initialized) {
    return <Loading />;
  }

  // No series projects exist
  if (seriesProjects.length === 0) {
    return (
      <div className="outline-page outline-page--no-story">
        <h2>No Series Projects</h2>
        <p>Series outlines are for planning multi-book arcs.</p>
        <p>Create a series project first, or convert an existing story to a series.</p>
      </div>
    );
  }

  // No project selected - show project selector
  if (!currentProject || !currentProject.is_series) {
    return (
      <div className="outline-page outline-page--no-story">
        <h2>Select a Series</h2>
        <p>Choose a series to view or create its outline:</p>
        <div className="outline-page__project-selector">
          {seriesProjects.map(project => (
            <button
              key={project.id}
              className="outline-page__project-btn"
              onClick={() => setCurrentProject(project)}
            >
              <span className="series-icon">📚</span>
              {project.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Get medium from project
  const medium = currentProject?.medium || 'novel';

  return (
    <div className="outline-page">
      <div className="outline-page__header">
        <select
          className="outline-page__project-dropdown"
          value={currentProject.id}
          onChange={(e) => {
            const project = seriesProjects.find(p => p.id === e.target.value);
            if (project) setCurrentProject(project);
          }}
        >
          {seriesProjects.map(project => (
            <option key={project.id} value={project.id}>
              📚 {project.name}
            </option>
          ))}
        </select>
      </div>
      <OutlinePanel projectId={currentProject.id} medium={medium} />
    </div>
  );
}

export default SeriesOutlinePage;
